/**
 * Every string of the confirm screen's entity plan, verbatim from the agreed copy: the ones
 * EntityPlanPanel renders itself, and the ones the caller uses to build its rows, primary label,
 * attestation and footer note, so all come from one place.
 *
 * CANDIDATE COPY, not yet approved (verifime-adr#618, COPY-572). It is listed for review and does
 * not block a merge. The marker covers `attestationLabel`, `SHARE_FOOTER_NOTE`,
 * `SHARE_WITH_PROVIDER_LABEL` and `FINISH_IT_LABEL`. `ALREADY_SHARED_LABEL` is new too, and is
 * marked PROPOSED COPY where it is defined (verifime-adr#665). Every other string here is as built.
 */
import { REQUEST_IDENTITY_CARD_COPY } from '../RequestScreens/RequestIdentityCard/requestIdentityCardCopy';
import { IdentityStatus } from '../types';

export type TEntityPlanRowStatus = 'toBeSubmitted' | 'submitted' | 'verified' | 'draft';

/**
 * PROPOSED COPY, awaiting Charles's approval (verifime-adr#665, ruling RL2).
 *
 * What a plan row says when its identity is already shared with this client. It reuses the words
 * "Already shared" that the request screens' identity card shows for the same fact
 * (requestIdentityCardCopy.ts), so the two screens agree. That string is candidate copy too
 * (verifime-adr#617): changing it there changes this screen, and the wording of both is for
 * review, not a merge gate. To give this screen its own wording, replace this line.
 */
export const ALREADY_SHARED_LABEL = REQUEST_IDENTITY_CARD_COPY.shared;

export const INDIVIDUAL_ROW_LABEL = 'Individual';

export const INDIVIDUAL_ROW_NAME = 'Your Individual Identity';

export const ENTITY_PLAN_STATUS_LABEL: Record<TEntityPlanRowStatus, string> = {
  toBeSubmitted: 'To be Submitted',
  submitted: 'Submitted',
  // As built: the identity status words Verified and Draft.
  verified: IdentityStatus.VERIFIED,
  draft: IdentityStatus.DRAFT,
};

/** The default name of an entity row, for example "Your Sole Trader Business". */
export const entityRowName = (entityLabel: string) => `Your ${entityLabel} Business`;

/** The primary button label for the row being started, for example "Start Individual Verification". */
export const startVerificationLabel = (rowLabel: string) => `Start ${rowLabel} Verification`;

/**
 * The primary button labels for the confirm step that shares a held identity with the provider, and
 * for finishing a Draft (618 R7, R10). The caller passes the one it needs as `primaryLabel`; the
 * panel decides nothing about it. They were story-local constants before this file held them.
 */
// CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
export const SHARE_WITH_PROVIDER_LABEL = 'Share with provider';
// CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
export const FINISH_IT_LABEL = 'Finish it';

export const CHANGE_ENTITY_TYPE_LINK = 'I want to change entity type';

/**
 * What the attestation calls the entity the person controls: its name when it has a usable one,
 * otherwise "this <type>" so the clause stays when the row has no usable name (618 L8: the
 * controller clause goes with any entity row). Nothing when the row gives neither.
 */
const controlledEntity = (entityName?: string, entityTypeLabel?: string) => {
  const name = entityName?.trim();
  if (name) return name;
  const type = entityTypeLabel?.trim();
  // CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
  return type ? `this ${type}` : '';
};

/**
 * The attestation's label (618 L2, L8): "I am <person>", and with an entity row the controller of
 * that entity too. Names are plain text. The entity is named by its declared name; a row with no
 * usable name (missing, empty, blank or failed validation) is named by its type label instead, so
 * the controller clause still shows. With neither a name nor a type the label is person-only. A
 * blank person name leaves out the person clause (no dangling "I am ."), and with no entity either
 * the label is empty: the caller decides whether to attest at all (only when the person is
 * established, L8), so it should not ask for an attestation it cannot name.
 */
export const attestationLabel = (
  personName: string,
  entityName?: string,
  entityTypeLabel?: string,
) => {
  const person = personName.trim();
  const entity = controlledEntity(entityName, entityTypeLabel);
  // CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
  if (person && entity) return `I am ${person}, and I am the controller of ${entity}.`;
  // CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
  if (person) return `I am ${person}.`;
  // CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
  if (entity) return `I am the controller of ${entity}.`;
  return '';
};

/**
 * The footer note for a step that shares or creates an identity (618 R7). The caller passes it as
 * `footerNote` on every such step, so the panel has no default of its own.
 */
// CANDIDATE COPY, not approved (verifime-adr#618, COPY-572)
export const SHARE_FOOTER_NOTE =
  'If you are not the person named here, or you are not acting for them, do not continue. If you think this is a mistake, contact your service provider.';
